// @owners { team: patients }
import { ActionSheetContext } from '@alto/design-system';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useContext } from 'react';
import { shouldShowRatingModal } from '~shared/features/rate-shipment/helpers';
import getLastDismissedRatingUuid from '~shared/features/rate-shipment/selectors/getLastDismissedRatingUuid';
import { getShouldPromptUserForPhiAuth } from '~shared/features/users/selectors/getUserPhiAuth';
import { fetchPendingRateShipment } from '~shared/queries/fetchPendingRateShipment';
import { useSelectorShared } from '~shared/store';
import { RateShipmentActionSheet } from '~web/features/post-delivery/components/RateShipmentActionSheet';
import { PhiAuthActionSheet } from '~web/features/profile/components/PhiAuthActionSheet';

/**
 * Hook that runs on post auth and shows the appropriate action sheet based on the user's phi preferences
 * and whether a pending rate shipment exists.
 * @returns { showPhiAuth: boolean, showRateShipment: boolean } - Whether the PhiAuthActionSheet or RateShipmentActionSheet should be shown.
 */
export const useShowPostAuthActionSheets = () => {
  const queryClient = useQueryClient();
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const showPhiAuth = useSelectorShared(getShouldPromptUserForPhiAuth);
  const lastDismissedRatingUuid = useSelectorShared(getLastDismissedRatingUuid);

  const handleShowPostAuthActionSheets = useCallback(async () => {
    if (showPhiAuth) {
      setActiveActionSheet(<PhiAuthActionSheet />);
      return { showPhiAuth: true, showRateShipment: false };
    } else {
      const pendingRating = await fetchPendingRateShipment(queryClient);
      if (shouldShowRatingModal(pendingRating, lastDismissedRatingUuid)) {
        setActiveActionSheet(<RateShipmentActionSheet />);
        return { showPhiAuth: false, showRateShipment: true };
      }
    }
    return { showPhiAuth: false, showRateShipment: false };
  }, [queryClient, setActiveActionSheet, showPhiAuth, lastDismissedRatingUuid]);

  return { handleShowPostAuthActionSheets };
};
